import { CellType, type GridSlicerEngine } from './GridSlicerEngine';

export class CanvasRenderer {
  private ctx: CanvasRenderingContext2D;

  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
  }

  public render(engine: GridSlicerEngine, kick: number, energy: number, screenShake: number): void {
    const cCtx = this.ctx;
    const w = engine.width;
    const h = engine.height;
    const cw = engine.cellWidth;
    const ch = engine.cellHeight;

    cCtx.save();

    if (screenShake > 0) {
      const shakeX = (Math.random() - 0.5) * 12 * screenShake;
      const shakeY = (Math.random() - 0.5) * 12 * screenShake;
      cCtx.translate(shakeX, shakeY);
    }

    // Background Void
    cCtx.fillStyle = '#03050d';
    cCtx.fillRect(0, 0, w, h);

    // Unclaimed Background Grid
    cCtx.strokeStyle = `rgba(0, 220, 255, ${0.05 + kick * 0.08})`;
    cCtx.lineWidth = 0.5;
    const gridStep = 20;
    for (let x = 0; x < w; x += gridStep) {
      cCtx.beginPath();
      cCtx.moveTo(x, 0);
      cCtx.lineTo(x, h);
      cCtx.stroke();
    }
    for (let y = 0; y < h; y += gridStep) {
      cCtx.beginPath();
      cCtx.moveTo(0, y);
      cCtx.lineTo(w, y);
      cCtx.stroke();
    }

    // Render Grid Matrix (CLAIMED, BORDER, TRAIL)
    for (let r = 0; r < engine.rows; r++) {
      for (let c = 0; c < engine.cols; c++) {
        const type = engine.grid[r][c];
        const cellX = c * cw;
        const cellY = r * ch;

        if (type === CellType.CLAIMED) {
          cCtx.fillStyle = `rgba(120, 0, 255, ${0.32 + kick * 0.1})`;
          cCtx.fillRect(cellX, cellY, cw + 0.5, ch + 0.5);
          cCtx.strokeStyle = 'rgba(180, 50, 255, 0.35)';
          cCtx.strokeRect(cellX, cellY, cw, ch);
        } else if (type === CellType.BORDER) {
          cCtx.fillStyle = 'rgba(0, 240, 255, 0.78)';
          cCtx.fillRect(cellX, cellY, cw + 0.5, ch + 0.5);
        } else if (type === CellType.TRAIL) {
          cCtx.fillStyle = engine.player.fastMode ? '#ff00aa' : '#ffbb00';
          cCtx.fillRect(cellX, cellY, cw + 0.5, ch + 0.5);
        }
      }
    }

    // Draw Power-Ups
    for (const pu of engine.powerUps) {
      cCtx.save();
      const color = pu.type === 'stasis' ? '#00ffff' : pu.type === 'turbo' ? '#ffbb00' : '#00ff66';
      const label = pu.type === 'stasis' ? 'S' : pu.type === 'turbo' ? 'T' : '+';
      cCtx.strokeStyle = color;
      cCtx.shadowColor = color;
      cCtx.shadowBlur = 12;
      cCtx.lineWidth = 2;
      cCtx.beginPath();
      cCtx.arc(pu.x, pu.y, pu.radius + Math.sin(performance.now() * 0.008) * 2, 0, Math.PI * 2);
      cCtx.stroke();

      cCtx.fillStyle = color;
      cCtx.font = 'bold 11px monospace';
      cCtx.textAlign = 'center';
      cCtx.textBaseline = 'middle';
      cCtx.fillText(label, pu.x, pu.y);
      cCtx.restore();
    }

    // Draw Open-Grid Sub-Drones (Scout Diamond, Hunter Triangle, Pulsar Hexagon)
    const stasisActive = engine.stasisTimer > 0;
    for (const d of engine.drones) {
      cCtx.save();
      cCtx.translate(d.x, d.y);
      cCtx.rotate(d.angle);

      const isStunned = d.stunTimer > 0 || stasisActive;
      const color = isStunned
        ? '#00ffff'
        : d.type === 'scout'
        ? '#00ffcc'
        : d.type === 'hunter'
        ? '#ff2288'
        : '#ffcc00';

      cCtx.strokeStyle = color;
      cCtx.fillStyle = 'rgba(10, 15, 30, 0.8)';
      cCtx.shadowColor = color;
      cCtx.shadowBlur = 10 + energy * 8;
      cCtx.lineWidth = 2;

      const r = d.radius * (d.type === 'pulsar' ? 1 + Math.sin(d.pulsePhase) * 0.2 : 1);
      cCtx.beginPath();
      if (d.type === 'scout') {
        // Rotating diamond
        cCtx.moveTo(0, -r);
        cCtx.lineTo(r, 0);
        cCtx.lineTo(0, r);
        cCtx.lineTo(-r, 0);
      } else if (d.type === 'hunter') {
        // Directional hunter triangle
        cCtx.moveTo(r * 1.2, 0);
        cCtx.lineTo(-r, r * 0.8);
        cCtx.lineTo(-r * 0.5, 0);
        cCtx.lineTo(-r, -r * 0.8);
      } else {
        // Oscillating hexagon pulsar
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2;
          if (i === 0) cCtx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
          else cCtx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
        }
      }
      cCtx.closePath();
      cCtx.fill();
      cCtx.stroke();
      cCtx.restore();
    }

    // Draw Qix Phantom Boss
    const qix = engine.qix;
    cCtx.save();
    const qixFrozen = qix.stunTimer > 0 || stasisActive;
    cCtx.strokeStyle = qixFrozen
      ? '#00ffff'
      : `rgba(255, 0, 100, ${0.8 + Math.sin(performance.now() * 0.01) * 0.2})`;
    cCtx.lineWidth = 2.5 + energy * 2;
    cCtx.shadowColor = qixFrozen ? '#00ffff' : '#ff0055';
    cCtx.shadowBlur = 12 + kick * 10;

    if (qix.points.length > 0) {
      cCtx.beginPath();
      cCtx.moveTo(qix.points[0].x, qix.points[0].y);
      for (let i = 1; i < qix.points.length; i++) {
        cCtx.lineTo(qix.points[i].x, qix.points[i].y);
      }
      cCtx.closePath();
      cCtx.stroke();

      cCtx.strokeStyle = qixFrozen ? 'rgba(0, 255, 255, 0.5)' : 'rgba(255, 200, 0, 0.6)';
      cCtx.lineWidth = 1;
      for (let i = 0; i < qix.points.length; i++) {
        cCtx.beginPath();
        cCtx.moveTo(qix.x, qix.y);
        cCtx.lineTo(qix.points[i].x, qix.points[i].y);
        cCtx.stroke();
      }
    }
    cCtx.restore();

    // Draw Border Sparks
    for (const spark of engine.sparks) {
      const sx = spark.x * cw + cw / 2;
      const sy = spark.y * ch + ch / 2;
      cCtx.save();
      cCtx.fillStyle = '#00ffff';
      cCtx.shadowColor = '#00ffff';
      cCtx.shadowBlur = 10;
      cCtx.beginPath();
      cCtx.arc(sx, sy, 5, 0, Math.PI * 2);
      cCtx.fill();
      cCtx.restore();
    }

    // Draw Fuse Particle if drawing and stationary
    const player = engine.player;
    if (player.isDrawing && player.fuseTimer > 0.5 && player.trail.length > 0) {
      const startCell = player.trail[0];
      const fx = startCell.x * cw + cw / 2;
      const fy = startCell.y * ch + ch / 2;
      cCtx.save();
      cCtx.fillStyle = '#ff3300';
      cCtx.shadowColor = '#ff6600';
      cCtx.shadowBlur = 15;
      cCtx.beginPath();
      cCtx.arc(fx, fy, 6 + Math.random() * 3, 0, Math.PI * 2);
      cCtx.fill();
      cCtx.restore();
    }

    // Draw Player Ship + Shield Aura
    if (player.invulnTimer === 0 || Math.floor(player.invulnTimer * 10) % 2 === 0) {
      const px = player.x * cw + cw / 2;
      const py = player.y * ch + ch / 2;
      cCtx.save();

      if (player.shieldActive) {
        cCtx.strokeStyle = '#00ff66';
        cCtx.shadowColor = '#00ff66';
        cCtx.shadowBlur = 10;
        cCtx.lineWidth = 2;
        cCtx.beginPath();
        cCtx.arc(px, py, 12, 0, Math.PI * 2);
        cCtx.stroke();
      }

      cCtx.fillStyle = player.isDrawing ? (player.fastMode ? '#ff00aa' : '#ffbb00') : '#00ffcc';
      cCtx.shadowColor = '#00ffcc';
      cCtx.shadowBlur = 12;

      cCtx.beginPath();
      cCtx.moveTo(px, py - 7);
      cCtx.lineTo(px + 6, py + 5);
      cCtx.lineTo(px - 6, py + 5);
      cCtx.closePath();
      cCtx.fill();

      cCtx.restore();
    }

    // Draw Shockwaves
    for (const sw of engine.shockwaves) {
      cCtx.save();
      cCtx.strokeStyle = sw.color;
      cCtx.shadowColor = sw.color;
      cCtx.shadowBlur = 12;
      cCtx.globalAlpha = sw.alpha;
      cCtx.lineWidth = 2.5;
      cCtx.beginPath();
      cCtx.arc(sw.x, sw.y, sw.radius, 0, Math.PI * 2);
      cCtx.stroke();
      cCtx.restore();
    }

    // Draw Particles
    for (const p of engine.particles) {
      cCtx.save();
      cCtx.fillStyle = p.color;
      cCtx.globalAlpha = p.alpha;
      cCtx.beginPath();
      cCtx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      cCtx.fill();
      cCtx.restore();
    }

    // Draw Floating Texts
    cCtx.save();
    cCtx.font = 'bold 13px monospace';
    cCtx.textAlign = 'center';
    for (const ft of engine.floatingTexts) {
      cCtx.fillStyle = ft.color;
      cCtx.shadowColor = ft.color;
      cCtx.shadowBlur = 6;
      cCtx.globalAlpha = ft.alpha;
      cCtx.fillText(ft.text, ft.x, ft.y);
    }
    cCtx.restore();

    // Top Progress Bar
    const barWidth = 240;
    const barHeight = 12;
    const barX = (w - barWidth) / 2;
    const barY = 16;

    cCtx.fillStyle = 'rgba(20, 20, 35, 0.8)';
    cCtx.fillRect(barX, barY, barWidth, barHeight);
    cCtx.strokeStyle = '#00f0ff';
    cCtx.strokeRect(barX, barY, barWidth, barHeight);

    const progress = Math.min(1, engine.claimedPercentage / engine.targetPercentage);
    cCtx.fillStyle = progress >= 1 ? '#00ff66' : '#00ccff';
    cCtx.fillRect(barX + 2, barY + 2, (barWidth - 4) * progress, barHeight - 4);

    cCtx.fillStyle = '#ffffff';
    cCtx.font = 'bold 12px monospace';
    cCtx.textAlign = 'center';
    cCtx.fillText(
      `CLAIMED: ${engine.claimedPercentage}% / TARGET: ${engine.targetPercentage}% · COMBO: ${engine.comboMultiplier}x`,
      w / 2,
      barY + barHeight + 15
    );

    // Game Over Overlay
    if (engine.isGameOver) {
      cCtx.fillStyle = 'rgba(5, 5, 15, 0.85)';
      cCtx.fillRect(0, 0, w, h);

      cCtx.fillStyle = '#ff0055';
      cCtx.font = 'bold 32px monospace';
      cCtx.textAlign = 'center';
      cCtx.fillText('SYSTEM OVERRIDE', w / 2, h / 2 - 25);

      cCtx.fillStyle = '#00ffff';
      cCtx.font = '16px monospace';
      cCtx.fillText(`FINAL SCORE: ${engine.score} · MAX COMBO: ${engine.maxCombo}x`, w / 2, h / 2 + 15);
      cCtx.fillText('PRESS [R] OR TAP [FIRE] TO RESTART', w / 2, h / 2 + 55);
    }

    cCtx.restore();
  }
}
